/**
 * iter478 — Revenue Budget Report.
 * Access: Admin, Senior Scheduler, Scheduler.
 *
 * iter490 — Refactor: extracted heavy components into
 * /pages/revenue-budget/* to keep this parent under ~450 lines.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Loader2, TrendingUp, PoundSterling, Plus, Check, Undo2, ChevronDown, Download, MoreHorizontal, History, Settings, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { _defaultRange, _defaultLastYearRange } from './revenue-budget/utils';
import { VarianceCell, MiniStat } from './revenue-budget/VarianceCell';
import { BudgetGrid } from './revenue-budget/BudgetGrid';
import { Section3Grid } from './revenue-budget/Section3Grid';
import { UpliftDialog } from './revenue-budget/UpliftDialog';
import { VersionDialog } from './revenue-budget/VersionDialog';
import { BulkActionDialog } from './revenue-budget/BulkActionDialog';
import { GrandTotalGrid } from './revenue-budget/GrandTotalGrid';
import { UndoLastPushDialog } from './revenue-budget/UndoLastPushDialog';
import { PublishHistoryDialog } from './revenue-budget/PublishHistoryDialog';
import { MasterPushConfigDialog } from './revenue-budget/MasterPushConfigDialog';

// iter495m — Combined Client + Site filter combobox. Types-ahead
// against every client and every site in the org; picking a site
// automatically clears the client filter (and vice-versa) so the
// backend gets a single, unambiguous filter.
function ClientSiteFilterPicker({ clients, sites, filters, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const selectedLabel = React.useMemo(() => {
    if (filters.site_id) {
      const s = sites.find(x => String(x.id) === String(filters.site_id));
      return s ? `Site: ${s.name}` : 'Site';
    }
    if (filters.client_id) {
      const c = clients.find(x => String(x.id) === String(filters.client_id));
      return c ? c.name : 'Client';
    }
    return 'All clients & sites';
  }, [filters, clients, sites]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="filter-client-site"
          className="h-6 text-[11px] w-56 border rounded px-2 flex items-center justify-between bg-white hover:bg-slate-50"
        >
          <span className="truncate text-left flex-1">{selectedLabel}</span>
          <span className="text-slate-400 text-[10px] ml-1">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-72" data-testid="filter-client-site-popover">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search clients or sites…"
            value={q}
            onValueChange={setQ}
            data-testid="filter-client-site-search"
          />
          <CommandList className="max-h-80">
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup heading="Reset">
              <CommandItem
                onSelect={() => { onChange({ client_id: '', site_id: '' }); setOpen(false); setQ(''); }}
                data-testid="filter-client-site-reset"
              >
                All clients &amp; sites
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading={`Clients (${clients.length})`}>
              {clients
                .filter(c => !q || c.name.toLowerCase().includes(q.toLowerCase()))
                .map(c => (
                  <CommandItem
                    key={`c-${c.id}`}
                    onSelect={() => { onChange({ client_id: String(c.id), site_id: '' }); setOpen(false); setQ(''); }}
                    data-testid={`filter-client-option-${c.id}`}
                  >
                    {c.name}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading={`Sites (${sites.length})`}>
              {sites
                .filter(s => !q
                  || s.name.toLowerCase().includes(q.toLowerCase())
                  || (s.client_name || '').toLowerCase().includes(q.toLowerCase()))
                .slice(0, 250)
                .map(s => (
                  <CommandItem
                    key={`s-${s.id}`}
                    onSelect={() => { onChange({ client_id: '', site_id: String(s.id) }); setOpen(false); setQ(''); }}
                    data-testid={`filter-site-option-${s.id}`}
                  >
                    <span className="text-slate-500 text-[10px] mr-1.5">{s.client_name}</span>
                    <span>{s.name}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function RevenueBudgetReportPage() {
  const { isAdmin, isSeniorScheduler, isScheduler } = useAuth();
  const { toast } = useToast();

  const defaultRange = useMemo(() => _defaultRange(), []);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [lastYearRange, setLastYearRange] = useState(() => _defaultLastYearRange(defaultRange.start, defaultRange.end));
  const [manualLastYear, setManualLastYear] = useState(false);
  const [filters, setFilters] = useState({ client_id: '', site_id: '' });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [sites, setSites] = useState([]);
  // iter480 — Phase 3 state.
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // iter489 — One-shot ref guard for the initial "collapse all Section 1"
  // default, so refetches don't clobber user-expanded groups.
  const defaultCollapsedAppliedRef = React.useRef(false);
  const [uplifts, setUplifts] = useState([]);
  const [showUpliftPanel, setShowUpliftPanel] = useState(false);
  // iter482 — Phase 4 state.
  const [versions, setVersions] = useState([]);
  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [showResetDraftDialog, setShowResetDraftDialog] = useState(false);
  const [resettingDraft, setResettingDraft] = useState(false);
  const [exporting, setExporting] = useState(false);
  // iter484 — Bulk state.
  // pendingDiscontinueIds is a Set of client_ids the user has toggled
  // for discontinuation in Section 2 (flushed on Save All).
  const [pendingDiscontinueIds, setPendingDiscontinueIds] = useState(new Set());
  // iter489 — Per-site discontinue queue (for orphan / Unassigned sites).
  const [pendingDiscontinueSiteIds, setPendingDiscontinueSiteIds] = useState(new Set());
  // iter495 — Unified Review & Publish dialog replaces the old
  // Save-All / Master-All / Save-&-Master trio.
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  // iter488 — Preview an uplift: highlight sites that will be affected.
  // Values: null | { scope:'global'|'client', client_id:number|null }
  const [previewUplift, setPreviewUplift] = useState(null);

  // iter494 — Undo Last Push state + loader.
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoBatch, setUndoBatch] = useState({ items: [], batch_at: null });

  // iter495e — Full publish history panel (multi-batch).
  const [historyOpen, setHistoryOpen] = useState(false);
  // iter495v — Customer Master syncing config dialog.
  const [pushConfigOpen, setPushConfigOpen] = useState(false);

  // iter495r — Track which rows have unsaved changes so we can nag
  // the user before Publish / navigation. Row-blur auto-flushes
  // pending edits, but a user pressing Publish while their caret is
  // still inside an input needs a clear "save first" prompt.
  const [dirtyRowIds, setDirtyRowIds] = useState(() => new Set());
  const dirtyCount = dirtyRowIds.size;
  const handleDirtyChange = useCallback((siteId, dirty) => {
    setDirtyRowIds(prev => {
      const hasIt = prev.has(siteId);
      if (dirty && hasIt) return prev;
      if (!dirty && !hasIt) return prev;
      const next = new Set(prev);
      if (dirty) next.add(siteId); else next.delete(siteId);
      return next;
    });
  }, []);
  useEffect(() => {
    if (dirtyCount === 0) return undefined;
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = `You have ${dirtyCount} unsaved row(s). Leave anyway?`;
      return e.returnValue;
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirtyCount]);

  const openUndoPreview = useCallback(async () => {
    setUndoLoading(true);
    setUndoOpen(true);
    try {
      const r = await api.get('/reports/revenue-budget/last-master-push');
      setUndoBatch({ items: r.data?.items || [], batch_at: r.data?.batch_at || null });
    } catch (err) {
      toast({ title: 'Could not load undo preview', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
      setUndoOpen(false);
    } finally { setUndoLoading(false); }
  }, [toast]);

  const commitUndo = useCallback(async () => {
    try {
      const r = await api.post('/reports/revenue-budget/last-master-push/undo');
      toast({ title: 'Undone', description: `${r.data?.count || 0} price change(s) cancelled.` });
      setUndoOpen(false);
      setUndoBatch({ items: [], batch_at: null });
      await load();
    } catch (err) {
      toast({ title: 'Undo failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  }, [toast]);

  // Derived stats for the Save-All / Master-All buttons.
  // iter494b — Stamp each site with its client_name so the bulk
  // dialogs can group rows visually.
  // iter495d — Notes-only rows no longer qualify as an "override"
  // (they're annotations, not master changes). Only real value
  // overrides (price / frequency / service / start-date) count.
  const overrideSites = useMemo(() => {
    const out = [];
    const collect = (rows) => (rows || []).forEach(g => (g.sites || []).forEach(s => {
      if (s.frequency_override || s.price_override != null
          || s.start_date_override || s.service_id_override) {
        out.push({ ...s, client_name: g.client_name || 'Unassigned' });
      }
    }));
    if (data) { collect(data.section1); collect(data.section2); }
    return out;
  }, [data]);
  const overrideCount = overrideSites.length;

  // iter493 — Sites whose master price would change purely because of
  // an active uplift (no explicit override set). Bulk-master flushes
  // these too when the user runs the combined action.
  const upliftPromotionSites = useMemo(() => {
    const out = [];
    const collect = (rows) => (rows || []).forEach(g => (g.sites || []).forEach(s => {
      if (s.price_override == null && s.uplift_target_price != null) {
        out.push({ ...s, client_name: g.client_name || 'Unassigned' });
      }
    }));
    if (data) { collect(data.section1); collect(data.section2); }
    return out;
  }, [data]);
  // iter495s — Sites carrying manual per-month £ overrides are skipped
  // by the bulk master push. Surface them in the Review dialog so the
  // scheduler sees exactly what will NOT touch the customer master.
  const monthOverrideSites = useMemo(() => {
    const out = [];
    const collect = (rows) => (rows || []).forEach(g => (g.sites || []).forEach(s => {
      if (s.has_month_overrides) {
        out.push({ ...s, client_name: g.client_name || 'Unassigned' });
      }
    }));
    if (data) { collect(data.section1); collect(data.section2); }
    return out;
  }, [data]);
  const hasAnyOverride = overrideCount > 0;
  const hasAnyUplift = upliftPromotionSites.length > 0;
  const hasAnyMasterable = hasAnyOverride || hasAnyUplift;

  // iter488 — Site counts for the uplift Applies-To preview counter.
  const siteCountByClient = useMemo(() => {
    const m = {};
    const collect = (rows) => (rows || []).forEach(g => {
      if (g.client_id) m[g.client_id] = (g.sites || []).length;
    });
    if (data) { collect(data.section1); collect(data.section2); }
    return m;
  }, [data]);
  const totalSiteCount = useMemo(() => (data?.totals?.sites_count || 0), [data]);
  const clientsWithClientUpliftIds = useMemo(() => (
    new Set(uplifts.filter(u => u.scope === 'client' && u.client_id).map(u => u.client_id))
  ), [uplifts]);
  // iter495c — Section 3 new-business rows are already persisted when
  // the user hits ✓ on each row; the Review & Publish dialog surfaces
  // them purely for visibility (no writes on publish).
  // iter495g — Only PENDING (never-published) Section 3 rows appear
  // in the dialog. Publish stamps them with the batch id, and Undo
  // releases them back to "pending".
  const section3Rows = useMemo(
    () => (data?.section3 || []).filter(r => r.published_batch_id == null),
    [data]
  );
  const section3Count = section3Rows.length;
  // iter489b — Flat list of Section 2 sites for the BulkActionDialog.
  const allSection2Sites = useMemo(() => {
    const list = [];
    (data?.section2 || []).forEach(g => (g.sites || []).forEach(s => list.push(s)));
    return list;
  }, [data]);

  const section2ToggleCount = pendingDiscontinueIds.size + pendingDiscontinueSiteIds.size;
  const hasSection2Toggle = section2ToggleCount > 0;

  const toggleClientDiscontinue = useCallback((clientId) => {
    setPendingDiscontinueIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);
  const toggleSiteDiscontinue = useCallback((siteId) => {
    setPendingDiscontinueSiteIds(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }, []);

  // iter495 — Single "Review & Publish" commit. The dialog decides
  // which of the three buckets (saves / overrides / uplifts) to
  // include. Each bucket that has anything to do runs in the same
  // round-trip so the user sees one toast summary.
  const commitReviewPublish = async ({ includeSaves, includeOverrides, includeUplifts }) => {
    let saveAffected = 0;
    if (includeSaves && pendingDiscontinueIds.size > 0) {
      const r = await api.post('/reports/revenue-budget/clients/bulk-discontinue', {
        client_ids: Array.from(pendingDiscontinueIds),
        discontinued: true,
      });
      saveAffected += r.data?.affected || 0;
      setPendingDiscontinueIds(new Set());
    }
    if (includeSaves && pendingDiscontinueSiteIds.size > 0) {
      const r = await api.post('/reports/revenue-budget/sites/bulk-discontinue', {
        site_ids: Array.from(pendingDiscontinueSiteIds),
        discontinued: true,
      });
      saveAffected += r.data?.affected || 0;
      setPendingDiscontinueSiteIds(new Set());
    }

    const site_ids = includeOverrides ? overrideSites.map(s => s.site_id) : [];
    const uplift_promotions = includeUplifts
      ? upliftPromotionSites.map(s => ({
          site_id: s.site_id,
          price: s.uplift_target_price,
          effective_from: s.uplift_effective_from,
          uplift_source_id: s.uplift_source_id,
        }))
      : [];

    let promoted = 0;
    let skippedMonth = [];
    // iter495g — Even when there is nothing in the site_ids/uplifts
    // buckets, still call bulk-promote when there are pending Section 3
    // rows so they get stamped with the batch id.
    if (site_ids.length > 0 || uplift_promotions.length > 0 || section3Count > 0) {
      const r = await api.post('/reports/revenue-budget/site-overrides/bulk-promote', {
        site_ids,
        apply_frequency: true, apply_price: true, apply_service: true,
        notes: 'Review & Publish',
        uplift_promotions: uplift_promotions.length > 0 ? uplift_promotions : undefined,
        // iter495k — Sites with no per-row start_date_override should
        // default to the budget window's start (e.g. 2027-01-01), not
        // today.
        default_effective_from: dateRange?.start,
      });
      promoted = r.data?.promoted || 0;
      skippedMonth = r.data?.skipped_month_override || [];
    }

    setShowReviewDialog(false);
    toast({
      title: 'Published',
      description: `${promoted} master change(s), ${saveAffected} Section 2 change(s), ${section3Count} new business.`,
    });
    await load();
  };

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups(g => ({ ...g, [key]: !g[key] }));
  }, []);

  const loadUplifts = useCallback(async () => {
    try {
      const r = await api.get('/reports/revenue-budget/uplifts');
      setUplifts(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadUplifts(); }, [loadUplifts]);

  const loadVersions = useCallback(async () => {
    try {
      const r = await api.get('/reports/revenue-budget/versions');
      setVersions(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const handleResetDraft = async () => {
    setResettingDraft(true);
    try {
      await api.delete('/reports/revenue-budget/draft');
      toast({ title: 'Draft reset', description: 'All overrides cleared. The budget now reflects master-data projections only.' });
      setShowResetDraftDialog(false);
      await load();
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setResettingDraft(false); }
  };

  const exportFile = async (kind) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
      }).toString();
      const resp = await api.get(
        `/reports/revenue-budget/export/${kind}?${qs}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], {
        type: kind === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue_budget_${dateRange.start}_${dateRange.end}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: `Export failed (${kind})`, description: err?.response?.data?.detail || String(err), variant: 'destructive' });
    } finally { setExporting(false); }
  };

  // Whenever the selected range changes, auto-shift the last-year range
  // (unless the user has manually overridden it).
  useEffect(() => {
    if (!manualLastYear) {
      setLastYearRange(_defaultLastYearRange(dateRange.start, dateRange.end));
    }
  }, [dateRange.start, dateRange.end, manualLastYear]);

  // Load filter dropdown values once from the report's own
  // filter-options endpoint (driven off the actual sites data).
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/reports/revenue-budget/filter-options');
        setClients(r.data?.clients || []);
        setServices(r.data?.services || []);
        setSites(r.data?.sites || []);
      } catch (e) { /* ignore — dropdowns just stay empty */ }
    })();
  }, []);

  const canView = isAdmin || isSeniorScheduler || isScheduler;
  const rangeIsValid = dateRange.start && dateRange.end && dateRange.start <= dateRange.end;

  // iter495t — Distinguish initial load from background refetch so
  // the grid is never unmounted during a save-triggered refetch (the
  // MonthCell Tab flow relies on the row staying mounted so its
  // lifted `editingYm` survives to open the next cell).
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async ({ background = false } = {}) => {
    // iter479 — Skip the fetch entirely for users without access so
    // the toast doesn't fire on top of the "Access denied." shell.
    if (!canView) { setLoading(false); return; }
    // iter494 — Never leave the spinner spinning: if the user has
    // mid-typed the date picker into an invalid range, drop out of
    // the loading state so the grid shell can render instead of the
    // "Loading budget…" spinner running forever.
    if (!rangeIsValid) { setLoading(false); return; }
    if (background) setRefreshing(true);
    else setLoading(true);
    try {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        last_year_start: lastYearRange.start,
        last_year_end: lastYearRange.end,
      };
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.site_id) params.site_id = filters.site_id;
      const r = await api.get('/reports/revenue-budget', { params });
      setData(r.data);
      // iter489 — On the very FIRST data arrival, default all Section 1
      // groups to COLLAPSED so the page opens tidy. Uses a ref guard so
      // refetches (e.g. after Save-All) never re-collapse user expansions.
      if (!defaultCollapsedAppliedRef.current && r.data?.section1?.length) {
        const collapsed = {};
        for (const g of r.data.section1) {
          const gkey = `revenue-budget-grid::${g.client_id ?? g.client_name}`;
          collapsed[gkey] = true;
        }
        setCollapsedGroups(prev => ({ ...prev, ...collapsed }));
        defaultCollapsedAppliedRef.current = true;
      }
    } catch (err) {
      toast({ title: 'Failed to load Revenue Budget', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      if (background) setRefreshing(false);
      else setLoading(false);
    }
  }, [dateRange, lastYearRange, filters, toast, rangeIsValid, canView]);

  // iter495t — Background refresh helper for save-triggered refetches.
  // Grid stays mounted; a subtle 'refreshing' pip surfaces near the
  // toolbar. Used by every child component that mutates via PATCH
  // (row-blur, MonthCell commit, uplift promotes, section 2 toggles).
  const refresh = useCallback(() => load({ background: true }), [load]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return <div className="p-6 text-slate-500">Access denied.</div>;
  }

  return (
    <div className="px-4 pt-4 pb-6 space-y-4" data-testid="revenue-budget-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <PoundSterling className="h-6 w-6" /> Revenue Budget
          </h1>
          <p className="text-xs text-slate-500 -mt-0.5">
            Forward-looking annual revenue plan from customer master.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {refreshing && (
            <span
              className="text-[10px] text-slate-500 flex items-center gap-1"
              data-testid="revenue-budget-refreshing"
              title="Refreshing budget in the background"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              refreshing
            </span>
          )}
          {/* iter495 — Single "Review & Publish" primary flow. */}
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              // iter495r — Nag the user if any row still has unsaved edits.
              // Row-blur normally saves; this catches the case where the
              // caret is still inside an input when they click Publish.
              if (dirtyCount > 0) {
                toast({
                  title: `${dirtyCount} row${dirtyCount === 1 ? '' : 's'} unsaved`,
                  description: 'Click outside the row (or hit the row\'s ✓ Save) to persist edits before publishing.',
                  variant: 'destructive',
                });
                return;
              }
              setShowReviewDialog(true);
            }}
            disabled={!hasSection2Toggle && !hasAnyMasterable && section3Count === 0 && dirtyCount === 0}
            data-testid="review-publish-btn"
            title={dirtyCount > 0
              ? `${dirtyCount} row(s) have unsaved edits — save them first`
              : 'Review every pending change and publish in one flow'}
          >
            <Check className="h-4 w-4 mr-1" /> Review &amp; Publish
            {(overrideCount + upliftPromotionSites.length + section2ToggleCount + section3Count) > 0 && (
              <span className="ml-2 rounded-full bg-white/25 text-white text-[10px] px-2 py-0.5 font-semibold">
                {overrideCount + upliftPromotionSites.length + section2ToggleCount + section3Count}
              </span>
            )}
            {dirtyCount > 0 && (
              <span className="ml-2 rounded-full bg-amber-500 text-white text-[10px] px-2 py-0.5 font-semibold" data-testid="unsaved-row-count">
                {dirtyCount} unsaved
              </span>
            )}
          </Button>

          {/* Secondary actions: uplifts, versions, undo, change log. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="more-menu-btn">
                <MoreHorizontal className="h-4 w-4 mr-1" /> More <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Pricing tools</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setShowUpliftPanel(true)} data-testid="open-uplift-panel-btn">
                <TrendingUp className="h-4 w-4 mr-2" /> Manage Uplifts
                {uplifts.length > 0 && (
                  <span className="ml-auto rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 font-semibold">
                    {uplifts.length}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowVersionPanel(true)} data-testid="open-version-panel-btn">
                <Plus className="h-4 w-4 mr-2" /> Lock &amp; Versions
                {versions.length > 0 && (
                  <span className="ml-auto rounded-full bg-slate-200 text-slate-800 text-[10px] px-2 py-0.5 font-semibold">
                    {versions.length}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Master push</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setPushConfigOpen(true)} data-testid="open-master-push-config-btn">
                <Settings className="h-4 w-4 mr-2" /> Customer Master syncing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Recovery</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setHistoryOpen(true)} data-testid="publish-history-btn">
                <History className="h-4 w-4 mr-2" /> Publish History
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openUndoPreview} data-testid="undo-last-push-btn" className="text-rose-700 focus:text-rose-800">
                <Undo2 className="h-4 w-4 mr-2" /> Undo Last Push
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Draft</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setShowResetDraftDialog(true)} data-testid="reset-draft-btn" className="text-rose-700 focus:text-rose-800">
                <RefreshCw className="h-4 w-4 mr-2" /> Reset Draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Export menu. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting} data-testid="export-menu-btn">
                <Download className="h-4 w-4 mr-1" /> Export <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportFile('xlsx')} disabled={exporting} data-testid="export-xlsx-btn">
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportFile('pdf')} disabled={exporting} data-testid="export-pdf-btn">
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* iter485 — Sticky top bar: shortcuts + compact filters + KPI ribbon.
          Sits at top-16 to clear the app-level sticky header (h-16). */}
      <div className="sticky top-16 z-30 -mx-4 px-4 pt-2 pb-2 bg-white/95 backdrop-blur border-b border-slate-200 space-y-2 shadow-sm">
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          <button type="button" onClick={() => document.getElementById('section1-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            data-testid="jump-section1">§1 · Defined frequency</button>
          <button type="button" onClick={() => document.getElementById('section2-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            data-testid="jump-section2">§2 · Adhoc</button>
          <button type="button" onClick={() => document.getElementById('section3-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            data-testid="jump-section3">§3 · New business</button>
          <div className="mx-2 h-4 w-px bg-slate-200" />
          {/* iter495q — Compact filters (Range + Client/Site).
              Region is controlled globally from the home page toggle —
              no in-report region picker anymore. */}
          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            Start
            <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(r => ({ ...r, start: e.target.value }))}
              className="h-6 text-[11px] w-32 py-0" data-testid="budget-start" />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            End
            <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(r => ({ ...r, end: e.target.value }))}
              className="h-6 text-[11px] w-32 py-0" data-testid="budget-end" />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            LY start
            <Input type="date" value={lastYearRange.start} onChange={(e) => { setManualLastYear(true); setLastYearRange(r => ({ ...r, start: e.target.value })); }}
              className="h-6 text-[11px] w-32 py-0" data-testid="ly-start" />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            LY end
            <Input type="date" value={lastYearRange.end} onChange={(e) => { setManualLastYear(true); setLastYearRange(r => ({ ...r, end: e.target.value })); }}
              className="h-6 text-[11px] w-32 py-0" data-testid="ly-end" />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            Client / Site
            {/* iter495m — Combined search: pick a client OR any of its
                sites in one dropdown. Backend accepts client_id + site_id
                and returns the same shape either way. */}
            <ClientSiteFilterPicker
              clients={clients}
              sites={sites}
              filters={filters}
              onChange={(next) => setFilters(f => ({ ...f, ...next }))}
            />
          </label>
          {manualLastYear && (
            <Button variant="outline" size="sm" onClick={() => { setManualLastYear(false); setLastYearRange(_defaultLastYearRange(dateRange.start, dateRange.end)); }}
              data-testid="reset-ly-btn"
              className="h-6 text-[10px] px-2 py-0">Reset LY</Button>
          )}
        </div>
        {!loading && data && (
          <div className="flex items-center gap-3 flex-wrap text-[11px]">
            <MiniStat label="Sites" value={data.totals?.sites_count || 0} money={false} />
            <MiniStat label="§1" value={data.totals?.section1_total} />
            <MiniStat label="§2" value={data.totals?.section2_total} />
            <MiniStat label="§3" value={data.totals?.section3_total} />
            <MiniStat label="Grand" value={data.totals?.grand_total} emphasize />
            <div className="flex items-center gap-1">
              <span className="text-slate-500">Δ vs LY:</span>
              <span className="text-sm font-semibold"><VarianceCell variance={data.totals?.variance || 0} /></span>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading budget…
        </div>
      )}

      {!loading && data && (
        <>
          <div id="section1-anchor" className="pt-2">
            <div className="mb-1.5 text-[11px] text-slate-600">
              <b>Section 1</b> — Sites with a defined audit frequency in the customer master. Values project forward from last audit using cadence (monthly / quarterly / etc). Overrides land in the site row and can be promoted to master with a chosen effective date.
            </div>
          </div>
          <BudgetGrid
            title="Section 1 — Sites with Defined Audit Frequency"
            testid="revenue-budget-grid"
            sectionLabel="Section 1"
            data={data}
            rows={data.section1 || []}
            months={data.months || []}
            showLastYear
            onDataChanged={refresh}
            dateRange={dateRange}
            collapsedGroups={collapsedGroups}
            toggleGroup={toggleGroup}
            previewUplift={previewUplift}
            onDirtyChange={handleDirtyChange}
          />

          <div id="section2-anchor" className="pt-2">
            <div className="mb-1.5 text-[11px] text-slate-600">
              <b>Section 2</b> — Adhoc / undefined-frequency sites; nil budget by default. Toggle a client&apos;s <b>Discontinue</b> pill to mark them for removal from budgeting — the change is applied in one go via <b>Save All</b>.
            </div>
          </div>
          <BudgetGrid
            title="Section 2 — Sites without Frequency (adhoc / undefined)"
            testid="revenue-budget-section2"
            sectionLabel="Section 2"
            data={data}
            rows={data.section2 || []}
            months={data.months || []}
            showLastYear
            onDataChanged={refresh}
            dateRange={dateRange}
            collapsedGroups={collapsedGroups}
            toggleGroup={toggleGroup}
            previewUplift={previewUplift}
            allowDiscontinue
            section2Toggles={Object.fromEntries(Array.from(pendingDiscontinueIds).map(id => [id, true]))}
            onToggleClientDiscontinue={toggleClientDiscontinue}
            pendingDiscontinueSiteIds={pendingDiscontinueSiteIds}
            onToggleSiteDiscontinue={toggleSiteDiscontinue}
            onDirtyChange={handleDirtyChange}
          />

          <div id="section3-anchor" className="pt-2">
            <div className="mb-1.5 text-[11px] text-slate-600">
              <b>Section 3</b> — New business / prospective clients not yet in the customer master. Add rows here to include their projected revenue in the plan before onboarding them formally.
            </div>
          </div>
          <Section3Grid
            months={data.months || []}
            rows={data.section3 || []}
            clients={clients}
            services={services}
            dateRange={dateRange}
            onChanged={refresh}
          />

          <div className="pt-2">
            <div className="mb-1.5 text-[11px] text-slate-600">
              <b>Grand total</b> — per-month roll-up of Sections 1 + 2 + 3, plus the full-year total.
            </div>
          </div>
          <GrandTotalGrid data={data} />
        </>
      )}

      {showReviewDialog && (
        <BulkActionDialog
          overrideSites={overrideSites}
          upliftPromotionSites={upliftPromotionSites}
          monthOverrideSites={monthOverrideSites}
          pendingDiscontinueIds={pendingDiscontinueIds}
          pendingDiscontinueSiteIds={pendingDiscontinueSiteIds}
          allSection2Sites={allSection2Sites}
          section3Rows={section3Rows}
          clients={clients}
          dateRange={dateRange}
          onCancel={() => setShowReviewDialog(false)}
          onConfirm={commitReviewPublish}
        />
      )}
      {showUpliftPanel && (
        <UpliftDialog
          uplifts={uplifts}
          clients={clients}
          sites={sites}
          dateRange={dateRange}
          siteCountByClient={siteCountByClient}
          totalSiteCount={totalSiteCount}
          clientsWithClientUpliftIds={clientsWithClientUpliftIds}
          previewUplift={previewUplift}
          onSetPreview={setPreviewUplift}
          onClose={() => { setShowUpliftPanel(false); setPreviewUplift(null); }}
          onChanged={async () => { await loadUplifts(); await load(); }}
        />
      )}
      {showVersionPanel && (
        <VersionDialog
          versions={versions}
          dateRange={dateRange}
          onClose={() => setShowVersionPanel(false)}
          onChanged={async () => { await loadVersions(); }}
          onDraftChanged={async () => { await loadVersions(); await load(); }}
        />
      )}
      {historyOpen && (
        <PublishHistoryDialog
          onClose={() => setHistoryOpen(false)}
          onChanged={load}
        />
      )}
      {/* Reset Draft confirmation */}
      <AlertDialog open={showResetDraftDialog} onOpenChange={setShowResetDraftDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the active draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>all overrides</strong> in the current draft — price overrides, frequency overrides, uplifts, month-level adjustments, and new business rows.
              <br /><br />
              The budget will revert to its baseline (master-data projections only). Locked versions and saved scenarios are not affected.
              <br /><br />
              <span className="text-rose-600 font-medium">This cannot be undone. Save a Scenario first if you want to keep a copy.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resettingDraft}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetDraft}
              disabled={resettingDraft}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-reset-draft-btn">
              {resettingDraft ? 'Resetting…' : 'Yes, reset draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {pushConfigOpen && (
        <MasterPushConfigDialog
          open={pushConfigOpen}
          onClose={() => setPushConfigOpen(false)}
          onSaved={() => { setPushConfigOpen(false); refresh(); }}
          clients={clients}
          services={services}
        />
      )}
      {undoOpen && (
        undoLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="undo-loading-overlay">
            <div className="bg-white rounded-lg px-4 py-3 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading last push…
            </div>
          </div>
        ) : (
          <UndoLastPushDialog
            items={undoBatch.items}
            batchAt={undoBatch.batch_at}
            onCancel={() => setUndoOpen(false)}
            onConfirm={commitUndo}
          />
        )
      )}
    </div>
  );
}
